import { ChangeDetectorRef, Component } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { LocationRequest } from './location.model';
import { LocationService } from './location.service';

@Component({
  selector: 'app-location',
  standalone: false,
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.css']
})
export class LocationComponent {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];

  locations: any[] = [];
  filteredLocations: any[] = [];

  currentPage = 1;
  pageSize = 5;

  activeTab = 'all';
  activeTabPopup = 1;
  searchText = '';

  isModalOpen = false;

  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  locationId = 0;
  isActive = true;

  nameKh = '';
  nameEn = '';

  constructor(
    private locationService: LocationService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadLanguages();
  }

  loadLanguages() {
    this.languageService.getAll().subscribe((res: any) => {
      this.languages = res.data.sort((a: any, b: any) => a.languageId - b.languageId);
      if (this.languages.length > 0) {
        this.setTab(this.languages[0].code);
        this.setTabPopup(this.languages[0].languageId);
      }

      this.loadLocations('');
    });
  }

  loadLocations(langCode: string) {
    this.locationService.getAllLocations(langCode).subscribe((res: any) => {
      const apiData: any[] = res.data || [];
      this.locations = apiData.flatMap(item =>
        item.translations.map((t: any) => ({
          locationId: item.locationId,
          isActive: item.isActive,
          languageCode: this.getLanguageCode(t.languageId),
          languageId: t.languageId,
          name: t.name
        }))
      );

      this.filterLocations(this.activeTab || 'kh');
    });
  }

  setTab(tab: string) {
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterLocations(tab);
  }

  setTabPopup(tab: number) {
    this.activeTabPopup = tab;
  }

  filterLocations(tab: string) {
    const keyword = this.searchText.trim().toLowerCase();
    this.currentPage = 1;

    this.filteredLocations = this.locations.filter(location => {
      const status = location.isActive ? 'Active' : 'Inactive';
      const matchesTab = location.languageCode === tab;
      const matchesSearch = !keyword ||
        `${location.name ?? ''} ${status} ${location.languageCode ?? ''}`
          .toLowerCase()
          .includes(keyword);

      return matchesTab && matchesSearch;
    });
  }

  getLanguageCount(lang: string): number {
    return this.locations.filter(r => r.languageCode === lang).length;
  }

  get pagedLocations(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredLocations.slice(start, end);
  }

  totalPages(): number {
    return Math.ceil(this.filteredLocations.length / this.pageSize);
  }

  changePage(page: number) {
    this.currentPage = page;
  }

  visibleButtonControl(eventName: string) {
    this.isEnableSave = false;
    this.isEnableUpdate = false;
    this.isEnableDelete = false;

    if (eventName === 'Add') { this.isEnableSave = true; }
    else if (eventName === 'Edit') { this.isEnableUpdate = true; }
    else if (eventName === 'Delete') { this.isEnableDelete = true; }
  }

  openModal(eventName?: string) {
    if (eventName) {
      this.visibleButtonControl(eventName);
    }
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  viewData(id: number, eventName: string) {
    this.locationId = id;

    this.locationService.getLocationById(id, '').subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          const data = res.data;
          this.locationId = data.locationId;
          this.isActive = data.isActive;
          this.nameKh = '';
          this.nameEn = '';

          data.translations.forEach((item: any) => {
            const languageCode = this.getLanguageCode(item.languageId);
            if (languageCode === 'kh') {
              this.nameKh = item.name;
            } else if (languageCode === 'en') {
              this.nameEn = item.name;
            }
          });

          this.openModal(eventName);
        } else {
          this.errorMessage = res.message;
        }

        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      },
      error: () => {
        this.errorMessage = 'Failed to load location';
        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      }
    });
  }

  resetForm() {
    this.locationId = 0;
    this.isActive = true;
    this.nameKh = '';
    this.nameEn = '';
  }

  getRequest(): LocationRequest {
    return {
      isActive: this.isActive,
      translations: [
        {
          languageId: 1,
          name: this.nameKh ?? ''
        },
        {
          languageId: 2,
          name: this.nameEn ?? ''
        }
      ]
    };
  }

  addNew() {
    this.resetForm();
    this.openModal('Add');
  }

  save() {
    const request = this.getRequest();
    this.clearMsg();
    this.locationService.create(request).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  update() {
    this.clearMsg();
    const request = this.getRequest();
    this.locationService.update(this.locationId, request).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  delete() {
    if (!confirm('Are you sure you want to delete ?')) {
      return;
    }

    this.locationService.delete(this.locationId).subscribe({
      next: (res) => this.refreshData(res.status, res.message),
      error: (err) => this.exceptionData(err)
    });
  }

  refreshData(status: string, message: string) {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadLocations('');
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  clearMsg() {
    this.successMessage = '';
    this.errorMessage = '';
  }

  exceptionData(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  private getLanguageCode(languageId: number): string {
    return this.languages.find(lang => lang.languageId === languageId)?.code ?? '';
  }
}
